import React, {EventHandler, HTMLAttributeAnchorTarget, useMemo, useState} from 'react'
import Link from 'next/link'
import Text from '@component/Text/Text'
import {useRouter} from 'next/router'
import {IMatchingCondition, MatchingType} from '@component/buttons/WizNavButton'
import {equalString} from '@util/strings'
import {createLog} from '@util/logs'
import {useIsomorphicLayoutEffect} from 'usehooks-ts'

type MouseEventHandler<T = Element> = EventHandler<React.MouseEvent<T>>

type Props = {
    text: string
    href: string
    target?: HTMLAttributeAnchorTarget | undefined
    className?: string
    textClassName?: string
    matchingCondition?: IMatchingCondition[]
    rightIcon?: React.ReactNode
    isScrolled?: boolean
    onClick?: MouseEventHandler<HTMLAnchorElement>
}

const NavButton: React.FC<Props> = ({
    text,
    href,
    target,
    className,
    textClassName,
    isScrolled,
    rightIcon,
    matchingCondition = [],
    onClick,
}) => {
    const router = useRouter()
    const [isActive, setIsActive] = useState(false)
    const currentPath = router?.asPath

    useIsomorphicLayoutEffect(() => {
        if (isMatchConditions()) {
            setIsActive(true)
        } else {
            setIsActive(false)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.asPath])

    const isMatchCondition = (condition: IMatchingCondition): boolean => {
        switch (condition.type) {
            case MatchingType.StartWith:
                if (router.pathname.startsWith(condition.url)) return true
                break
            case MatchingType.Equal:
                if (equalString(router.pathname, condition.url)) {
                    if (router.pathname === condition.url) {
                        for (let key in condition.query) {
                            if (!equalString(router.query[key]?.toString(), condition.query[key])) return false
                        }
                        return true
                    }
                }
                break
        }

        return false
    }

    const isMatchConditions = (): boolean => {
        for (const condition of matchingCondition) {
            if (isMatchCondition(condition)) {
                return true
            }
        }

        return false
    }

    const getActiveColor = useMemo(() => {
        if (isActive) {
            return 'text-primary dark:text-dark_primary'
        } else {
            if (textClassName) {
                return textClassName
            } else {
                return 'text-gray01 dark:text-dark_gray01'
            }
        }
    }, [isActive, textClassName])

    return (
        <Link
            target={target}
            href={href}
            shallow={true}
            onClick={e => {
                if (href === currentPath && e?.cancelable) {
                    e?.preventDefault()
                } else {
                    onClick && onClick(e)
                    createLog('event', 'navigation_click_nav_menu', {path: text})
                }
            }}
            className={className}
            suppressHydrationWarning={true}>
            <div className={'flex items-center gap-x-[3px]'}>
                <Text className={`cursor-pointer text-btn_sm ${getActiveColor}`}>{text}</Text>
                {rightIcon}
            </div>
        </Link>
    )
}

export default NavButton
